import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../contractorApi';

const TASK_BANNER = {
    chatting:            { text: '顧客と詳細を調整し、見積書を提出しましょう', type: 'action', action: 'estimate', actionLabel: '見積書を作成する' },
    estimated:           { text: '見積書を提出済みです。顧客の回答をお待ちください', type: 'info' },
    decided:             { text: '受注が確定しました！請求書を提出してください', type: 'decided', action: 'invoice', actionLabel: '請求書を作成する' },
    invoiced:            { text: '請求書を提出済みです。入金をお待ちください', type: 'info' },
    paid:                { text: '入金確認済み。工事日程を登録してください', type: 'action', action: 'schedule', actionLabel: '日程を登録する' },
    scheduled:           { text: '日程確定済み。工事を開始してください', type: 'info' },
    in_progress:         { text: '工事中です。完了後に完了通知を送ってください', type: 'action', action: 'complete', actionLabel: '完了通知を送る' },
    completion_notified: { text: '完了通知を送りました。顧客の確認をお待ちください', type: 'info' },
    completed:           { text: 'すべての手続きが完了しました。ありがとうございました！', type: 'done' },
};

const INNER = { maxWidth: 800, margin: '0 auto', padding: '0 20px' };

// ── アイコン ──────────────────────────────────────────────────────
const FileTextIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
    </svg>
);
const InfoIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
);
const SendIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M22 2L11 13" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
        <path d="M22 2L15 22l-4-9-9-4 20-7z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// ── バブル ────────────────────────────────────────────────────────
const Bubble = ({ msg, isMe }) => {
    const time = msg.created_at
        ? new Date(msg.created_at).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
        : '';
    return (
        <div style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
            <div style={{ maxWidth: '72%', display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
                {!isMe && <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginBottom: 3, marginLeft: 4 }}>顧客</div>}
                <div style={{
                    background: isMe ? '#141c3d' : '#fff',
                    color: isMe ? '#fff' : '#1a1a2e',
                    borderRadius: isMe ? '16px 0 16px 16px' : '0 16px 16px 16px',
                    padding: '10px 14px', fontSize: '0.9rem',
                    border: isMe ? 'none' : '1px solid #e0e4ea',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    lineHeight: 1.55, whiteSpace: 'pre-wrap',
                }}>
                    {msg.content}
                </div>
                {time && <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: 3, [isMe ? 'marginRight' : 'marginLeft']: 4 }}>{time}</div>}
            </div>
        </div>
    );
};

// ── バナー（カード部分のみ） ───────────────────────────────────────
const TaskBannerCard = ({ banner, projectId, navigate }) => {
    if (!banner) return null;
    const isDecided = banner.type === 'decided';
    const isAction = banner.type === 'action';
    const isDone = banner.type === 'done';
    const bg = isDecided ? '#fffbeb' : isAction ? '#fff8e1' : isDone ? '#e8f5e9' : '#f0f9ff';
    const border = isDecided ? '#f59e0b' : isAction ? '#fbbf24' : isDone ? '#81c784' : '#90caf9';
    const textColor = isDecided ? '#78350f' : isAction ? '#92400e' : isDone ? '#1b5e20' : '#1e40af';
    const iconColor = isDecided ? '#f59e0b' : isAction ? '#f59e0b' : isDone ? '#2e7d32' : '#3b82f6';

    const handleAction = async () => {
        if (banner.action === 'estimate') navigate(`/contractor/estimate/create/${projectId}`);
        else if (banner.action === 'invoice') navigate(`/contractor/invoice/create/${projectId}`);
        else if (banner.action === 'schedule') navigate(`/contractor/schedule/${projectId}`);
        else if (banner.action === 'complete') {
            await api.post(`projects/${projectId}/notify_completion`);
            window.location.reload();
        }
    };

    return (
        <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 8, padding: isDecided ? '14px 16px' : '10px 14px', gap: 10 }}>
            {isDecided && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                    <span style={{ fontSize: '1rem' }}>🎉</span>
                    <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#92400e' }}>受注確定おめでとうございます！</span>
                    <span style={{ padding: '1px 8px', background: '#f59e0b', color: '#fff', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700 }}>受注確定</span>
                </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                    <span style={{ color: iconColor }}>{isAction || isDecided ? <FileTextIcon /> : <InfoIcon />}</span>
                    <span style={{ fontSize: '0.84rem', fontWeight: 500, color: textColor }}>{banner.text}</span>
                </div>
                {(isAction || isDecided) && (
                    <button onClick={handleAction} style={{ padding: '6px 12px', background: isDecided ? '#f59e0b' : '#141c3d', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
                        {banner.actionLabel}
                    </button>
                )}
            </div>
        </div>
    );
};

// ── markSeen ──────────────────────────────────────────────────────
const markSeen = (projectId) => {
    const seen = JSON.parse(localStorage.getItem('contractor_seen_projects') || '[]');
    if (!seen.includes(projectId)) {
        localStorage.setItem('contractor_seen_projects', JSON.stringify([...seen, projectId]));
    }
};

// ── メイン ────────────────────────────────────────────────────────
const ContractorChatPage = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const contractorId = parseInt(localStorage.getItem('contractor_id') || '0', 10);

    const [project, setProject] = useState(null);
    const [rejectedEstimate, setRejectedEstimate] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const bottomRef = useRef(null);
    const lastMsgCount = useRef(0);
    const textareaRef = useRef(null);

    useEffect(() => { markSeen(Number(projectId)); }, [projectId]);

    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    }, [text]);

    const fetchProject = useCallback(() => {
        api.get(`projects/${projectId}`).then(r => {
            const p = r.data;
            setProject(p);
            // chatting状態のとき、差し戻し済み見積書を確認する
            if (p.status === 'chatting') {
                api.get(`estimates/by_project/${projectId}`)
                    .then(er => {
                        if (er.data.status === 'rejected') setRejectedEstimate(er.data);
                        else setRejectedEstimate(null);
                    })
                    .catch(() => setRejectedEstimate(null));
            } else {
                setRejectedEstimate(null);
            }
        }).catch(console.error);
    }, [projectId]);

    useEffect(() => {
        fetchProject();
        const t = setInterval(fetchProject, 5000);
        return () => clearInterval(t);
    }, [fetchProject]);

    const fetchMessages = useCallback(() => {
        api.get(`messages/by_project/${projectId}`).then(r => setMessages(r.data)).catch(console.error);
    }, [projectId]);

    useEffect(() => {
        fetchMessages();
        const t = setInterval(fetchMessages, 3000);
        return () => clearInterval(t);
    }, [fetchMessages]);

    useEffect(() => {
        if (messages.length > lastMsgCount.current) {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
            lastMsgCount.current = messages.length;
        }
    }, [messages]);

    const sendMessage = async () => {
        if (!text.trim() || sending) return;
        setSending(true);
        try {
            await api.post('messages', { project_id: Number(projectId), content: text.trim(), user_type: 'contractor', user_id: contractorId });
            setText('');
            if (textareaRef.current) textareaRef.current.style.height = 'auto';
            fetchMessages();
        } catch (e) { console.error(e); }
        finally { setSending(false); }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); sendMessage(); }
    };

    if (!project) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100dvh' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #e0e4ea', borderTopColor: '#141c3d', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );

    const status = project.status;
    const banner = TASK_BANNER[status];
    const customerName = project.customer
        ? `${project.customer.last_name} ${project.customer.first_name} 様`
        : '顧客';

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', flexDirection: 'column', background: '#f5f6f8' }}>

            {/* ヘッダー — 全幅、内側 max-width */}
            <div style={{ background: '#141c3d', flexShrink: 0 }}>
                <div style={{ ...INNER, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <button onClick={() => navigate(`/contractor/project/${projectId}`)} style={{ background: 'none', border: 'none', color: '#fff', padding: 0, fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 }}>‹</button>
                        <div style={{ fontWeight: 700, fontSize: '1rem', color: '#fff' }}>
                            チャット：{customerName}
                        </div>
                    </div>
                    <button
                        onClick={() => navigate(`/contractor/project/${projectId}`)}
                        style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', borderRadius: 20, padding: '5px 12px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}
                    >
                        案件詳細
                    </button>
                </div>
            </div>

            {/* メッセージエリア — スクロール、内側 max-width */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
                <div style={{ ...INNER, padding: '24px 20px', display: 'flex', flexDirection: 'column' }}>
                    {messages.length === 0 && (
                        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem', marginTop: 40 }}>
                            まだメッセージはありません。<br />顧客に挨拶してみましょう！
                        </div>
                    )}
                    {messages.map((msg, i) => (
                        <Bubble key={msg.id || i} msg={msg} isMe={msg.user_type === 'contractor'} />
                    ))}
                    <div ref={bottomRef} />
                </div>
            </div>

            {/* 差し戻しバナー（chatting + 差し戻し済み見積書がある場合） */}
            {rejectedEstimate && (
                <div style={{ flexShrink: 0 }}>
                    <div style={{ ...INNER, padding: '0 20px 10px' }}>
                        <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ fontSize: '0.84rem', fontWeight: 600, color: '#92400e', margin: '0 0 2px' }}>見積書が差し戻されました</p>
                                {rejectedEstimate.rejection_reason && (
                                    <p style={{ fontSize: '0.78rem', color: '#78350f', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {rejectedEstimate.rejection_reason}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={() => navigate(`/contractor/estimate/create/${projectId}`)}
                                style={{ padding: '6px 12px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}
                            >
                                修正して再送する
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* バナー — グレー背景上、入力欄直上 */}
            {banner && !rejectedEstimate && (
                <div style={{ flexShrink: 0 }}>
                    <div style={{ ...INNER, padding: '0 20px 10px' }}>
                        <TaskBannerCard banner={banner} projectId={projectId} navigate={navigate} />
                    </div>
                </div>
            )}

            {/* 入力エリア — 全幅、内側 max-width */}
            <div style={{ background: '#fff', borderTop: '1px solid #e0e4ea', flexShrink: 0 }}>
                <div style={{ ...INNER, padding: '10px 20px', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <textarea
                        ref={textareaRef}
                        rows={1}
                        placeholder="メッセージを入力… (Shift+Enterで送信)"
                        value={text}
                        onChange={e => setText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        style={{ flex: 1, resize: 'none', padding: '10px 14px', fontSize: '0.9rem', border: '1px solid #e0e4ea', borderRadius: 8, outline: 'none', lineHeight: 1.5, overflowY: 'auto', minHeight: 44, maxHeight: 160, boxSizing: 'border-box' }}
                    />
                    <button
                        onClick={sendMessage}
                        disabled={!text.trim() || sending}
                        style={{ width: 44, height: 44, borderRadius: 8, background: text.trim() ? '#141c3d' : '#e0e4ea', border: 'none', cursor: text.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.15s' }}
                    >
                        {sending
                            ? <div style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                            : <SendIcon />}
                    </button>
                </div>
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default ContractorChatPage;
